import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { schoolInfo, classes } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    classInterest: '',
    experience: '',
    message: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send booking data to backend API
      const response = await axios.post(`${API}/trial-class-booking`, formData);
      
      if (response.data.success) {
        toast.success("Thank you for your trial class booking! Your information has been sent to vaani.sargam.mw@gmail.com. Snehlata will contact you within 24 hours to schedule your trial class.");
        setFormData({
          name: '',
          email: '',
          phone: '',
          age: '',
          classInterest: '',
          experience: '',
          message: ''
        });
      } else {
        toast.error("There was an error submitting your booking. Please contact us directly at vaani.sargam.mw@gmail.com");
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("There was an error submitting your booking. Please contact us directly at vaani.sargam.mw@gmail.com or call +1-647-678-8585");
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800">
              🎵 Get in Touch
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to begin your musical journey? We'd love to hear from you and help you find the perfect class
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Get in Touch
                </h2>
                <p className="text-gray-600 mb-8">
                  Have questions about our classes or want to schedule a trial? We're here to help you start your musical journey.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="border-blue-100 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-100 p-3 rounded-full">
                        <Phone className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Phone</h3>
                        <p className="text-gray-600">{schoolInfo.phone}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-blue-100 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-100 p-3 rounded-full">
                        <Mail className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Email</h3>
                        <p className="text-gray-600">{schoolInfo.email}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-blue-100 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-blue-100 p-3 rounded-full">
                        <MapPin className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Address</h3>
                        <p className="text-gray-600">{schoolInfo.address}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-blue-100 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-blue-100 p-3 rounded-full">
                        <Clock className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Class Hours</h3>
                        <div className="text-gray-600 space-y-1">
                          <p>Monday - Friday: 4:00 PM - 8:00 PM</p>
                          <p>Saturday: 10:00 AM - 6:00 PM</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Map Section */}
              <div className="mt-8">
                <Card className="border-blue-100 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="bg-blue-50 p-4 border-b border-blue-100">
                      <h3 className="font-semibold text-gray-900 flex items-center">
                        <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                        Find Us on Map
                      </h3>
                    </div>
                    <div className="h-64">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2886.4447418687344!2d-79.68759368450171!3d43.47505607912847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b5d7d5d5d5d5d%3A0x5d5d5d5d5d5d5d5d!2s91%20McCraney%20St%20W%2C%20Oakville%2C%20ON%20L6H%201H5%2C%20Canada!5e0!3m2!1sen!2sus!4v1234567890123"
                        width="100%"
                        height="256"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Vaani Sargam Music World Location"
                      ></iframe>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-blue-100 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-900">
                    Book Your Trial Class
                  </CardTitle>
                  <p className="text-gray-600">
                    Fill out the form below and we'll get back to you within 24 hours to schedule your trial class.
                  </p>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="Enter your full name"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="Enter your email"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder="Enter your phone number"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="age">Age</Label>
                        <Input
                          id="age"
                          value={formData.age}
                          onChange={(e) => handleInputChange('age', e.target.value)}
                          placeholder="Enter age (or student's age)"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="classInterest">Class of Interest</Label>
                        <Select value={formData.classInterest} onValueChange={(value) => handleInputChange('classInterest', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a class" />
                          </SelectTrigger>
                          <SelectContent>
                            {classes.map((classItem) => (
                              <SelectItem key={classItem.id} value={classItem.name}>
                                {classItem.name}
                              </SelectItem>
                            ))}
                            <SelectItem value="general-inquiry">General Inquiry</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="experience">Music Experience</Label>
                        <Select value={formData.experience} onValueChange={(value) => handleInputChange('experience', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beginner">Complete Beginner</SelectItem>
                            <SelectItem value="some-experience">Some Experience</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        placeholder="Tell us more about your musical goals or any specific questions you have..."
                        rows={4}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <Send className="h-5 w-5 mr-2" />
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-sky-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about our classes and enrollment process
            </p>
          </div>

          <div className="space-y-6">
            <Card className="bg-white border-blue-100">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <CheckCircle className="h-5 w-5 text-blue-500 mr-2" />
                  Do I need prior musical experience?
                </h3>
                <p className="text-gray-600 ml-7">
                  Not at all! We welcome complete beginners and have programs designed for all skill levels, from first-time students to advanced practitioners.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-blue-100">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <CheckCircle className="h-5 w-5 text-blue-500 mr-2" />
                  What is the trial class like?
                </h3>
                <p className="text-gray-600 ml-7">
                  Our trial class is a 30-minute session where you'll meet your potential teacher, learn about the program structure, and try some basic exercises. It's completely free!
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-blue-100">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <CheckCircle className="h-5 w-5 text-blue-500 mr-2" />
                  Do I need to bring any instruments?
                </h3>
                <p className="text-gray-600 ml-7">
                  For vocal classes, no instruments are needed. For instrumental classes, we can provide basic instruments during trial classes. We'll advise you on instrument acquisition once you enroll.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;